import Assumption from "./Assumption.js";
import NestedMap from "../../NestedMap.js";
import RuleModelToAssumptionSpecsVisitor from "./RuleModelToAssumptionSpecsVisitor.js";

/**
 * Convert a bunch of instances of {@link RuleTemplate} into instances
 * of {@link Assumption}. Usually, a rule template is converted to an
 * assumption but in a specific case, two rule templates on the same
 * cell can be converted to a single assumption.
 *
 * @see {@link Assumption} for differences with {@link RuleTemplate}.
 * @param {RuleTemplate[]} ruleTemplates - A collection of rules to
 *   convert to assumptions.
 */
export default class RuleTemplatesToAssumptionsConverter {
	convert({ ruleTemplates }) {
		// The map uses arrays of [rowId, year, month] as keys and
		// arrays of instances of RuleTemplate as values.
		const ruleTemplatesByCell =
			this._groupRuleTemplatesInMap(ruleTemplates);

		return [
			...this._convertRuleTemplateMapToAssumptions(ruleTemplatesByCell),
		];
	}

	_groupRuleTemplatesInMap(ruleTemplates) {
		const map = new NestedMap();

		for (const ruleTemplate of ruleTemplates) {
			const key = [
				ruleTemplate.getRowId(),
				ruleTemplate.getStart().getYear(),
				ruleTemplate.getStart().getCalendarIndex(),
			];

			let ruleTemplatesInSameCell = map.get(key);

			if (!ruleTemplatesInSameCell) {
				ruleTemplatesInSameCell = [];
				map.set(key, ruleTemplatesInSameCell);
			}

			ruleTemplatesInSameCell.push(ruleTemplate);
		}

		return map;
	}

	*_convertRuleTemplateMapToAssumptions(ruleTemplateByCell) {
		const endDateByRuleTemplate = this._computeEndDates(ruleTemplateByCell);

		for (const ruleTemplatesOfSingleCell of ruleTemplateByCell.values()) {
			yield* this._convertRuleTemplatesToAssumptions({
				ruleTemplatesOfSingleCell,
				endDateByRuleTemplate,
			});
		}
	}

	/**
	 * The rule templates here are expected to have the same start and
	 * row id.
	 *
	 * @param {RuleTemplate[]} ruleTemplatesOfSingleCell - .
	 * @param {Map<RuleTemplate, MonthPeriod|Infinity>} endDateByRuleTemplate - Contains the date at which the rule template stops impacting predictions
	 * @return {Assumption[]}
	 */
	_convertRuleTemplatesToAssumptions({
		ruleTemplatesOfSingleCell,
		endDateByRuleTemplate,
	}) {
		const visitor = new RuleModelToAssumptionSpecsVisitor();

		const balanceRuleModel = ruleTemplatesOfSingleCell
			.find((ruleTemplate) => ruleTemplate.isBalance())
			?.getRuleModel();

		return ruleTemplatesOfSingleCell.flatMap((ruleTemplate) =>
			visitor.visit(ruleTemplate.getRuleModel()).map((assumptionSpec) =>
				this._makeAssumption({
					ruleTemplate,
					assumptionSpec,
					balanceRuleModel,
					end: endDateByRuleTemplate.get(ruleTemplate),
				}),
			),
		);
	}

	_makeAssumption({ ruleTemplate, assumptionSpec, balanceRuleModel, end }) {
		const assumption = new Assumption({
			rowId: ruleTemplate.getRowId(),
			start: ruleTemplate.getStart(),
			end,
			ruleModelClass: ruleTemplate.getRuleModel().constructor,
			...assumptionSpec,
		});

		if (balanceRuleModel && ruleTemplate.isPrediction()) {
			assumption.readSettingsFromBalanceRuleModel(balanceRuleModel);
		}

		return assumption;
	}

	_computeEndDates(ruleTemplateByCell) {
		const endDateByRuleTemplate = new Map();

		this._computeEndDatesOfPredictionRules({
			ruleTemplateByCell,
			endDateByRuleTemplate,
		});

		this._computeEndDatesOfManualEntryRules({
			ruleTemplateByCell,
			endDateByRuleTemplate,
		});

		this._computeEndDatesOfManualJournalEntryRules({
			ruleTemplateByCell,
			endDateByRuleTemplate,
		});

		return endDateByRuleTemplate;
	}

	_computeEndDatesOfPredictionRules({
		ruleTemplateByCell,
		endDateByRuleTemplate,
	}) {
		for (const [rowId] of ruleTemplateByCell.keys({ length: 1 })) {
			const ruleTemplatesOfRow = this._getPredictionRuleTemplatesOfRow({
				ruleTemplateByCell,
				rowId,
			});

			this._computeEndDatesOfPredictionRulesInRow({
				ruleTemplatesOfRow,
				endDateByRuleTemplate,
			});
		}
	}

	_getPredictionRuleTemplatesOfRow({ ruleTemplateByCell, rowId }) {
		return [...ruleTemplateByCell.get([rowId]).values()]
			.flat()
			.filter((ruleTemplate) => ruleTemplate.isPrediction());
	}

	_computeEndDatesOfPredictionRulesInRow({
		ruleTemplatesOfRow,
		endDateByRuleTemplate,
	}) {
		const sortedRuleTemplates = ruleTemplatesOfRow.sort(
			(ruleTemplate1, ruleTemplate2) =>
				ruleTemplate1.getStart().compareTo(ruleTemplate2.getStart()),
		);

		for (let i = 0; i <= sortedRuleTemplates.length - 1; i++) {
			const nextRuleTemplate =
				i < sortedRuleTemplates.length - 1 &&
				sortedRuleTemplates[i + 1];

			endDateByRuleTemplate.set(
				sortedRuleTemplates[i],
				nextRuleTemplate
					? nextRuleTemplate.getStart().add(-1)
					: Infinity,
			);
		}
	}

	_computeEndDatesOfManualEntryRules({
		ruleTemplateByCell,
		endDateByRuleTemplate,
	}) {
		for (const ruleTemplates of ruleTemplateByCell.values()) {
			for (const ruleTemplate of ruleTemplates) {
				if (ruleTemplate.isManualEntry()) {
					endDateByRuleTemplate.set(
						ruleTemplate,
						ruleTemplate.getStart(),
					);
				}
			}
		}
	}

	_computeEndDatesOfManualJournalEntryRules({
		ruleTemplateByCell,
		endDateByRuleTemplate,
	}) {
		for (const ruleTemplates of ruleTemplateByCell.values()) {
			for (const ruleTemplate of ruleTemplates) {
				if (ruleTemplate.isManualJournalEntry()) {
					const ruleModel = ruleTemplate.getRuleModel();

					const endDate = ruleModel.hasInfinitelyRollingMonths()
						? Infinity
						: ruleTemplate
								.getStart()
								.add(ruleModel.getNumberOfRollingMonths() - 1);

					endDateByRuleTemplate.set(ruleTemplate, endDate);
				}
			}
		}
	}

	static convert({ ruleTemplates }) {
		return new this().convert({ ruleTemplates });
	}
}
